<template>
  <div class="main-navbar fixed" :class="{ dark: isDarkMode }">

    <div v-if="editor.showSrtEditor.value" class="editor-bar fixed top-0 left-0 right-0 h-16 z-40" :class="{ 'editor-bar-compact': !isDesktop }">
      <span class="editor-tag">{{ $t('Transcribe') }}</span>
      <div v-if="currentTranscriptSource && currentTranscriptSource.sourceId" class="editor-drafts">
        <DraftSelector
          :sourceId="currentTranscriptSource.sourceId"
          :editor="editor"
          @draft-selected="handleDraftSelected"
          @draft-created="handleDraftCreated"
          @draft-deleted="handleDraftDeleted"
        />
      </div>
      <button class="editor-close" @click="closeSrtEditor()" :aria-label="$t('Close')">
        <i class="fas fa-times" aria-hidden="true"></i>
        <span>{{ $t('Close') }}</span>
      </button>
    </div>

    <template v-else>

      <header v-if="isDesktop" class="topbar fixed top-0 left-0 right-0 h-16 z-40">
        <div class="bar-inner">

          <button class="brand" @click="navigateTo('Home', $event)" :title="$t('Home')">
            <span class="brand-mark" aria-hidden="true">
              <span class="bm-bar"></span><span class="bm-bar"></span><span class="bm-bar"></span>
            </span>
            <span class="brand-word">Transcriptor</span>
          </button>

          <nav v-if="!isLoggedIn" class="nav-links" aria-label="Primary">
            <button class="nav-link" :class="{ active: isPathActive('/home') }" :aria-current="isPathActive('/home') ? 'page' : undefined" @click="navigateTo('Home', $event)">{{ $t('Home') }}</button>
            <button class="nav-link" :class="{ active: isPathActive('/about') }" :aria-current="isPathActive('/about') ? 'page' : undefined" @click="navigateTo('About', $event)">{{ $t('About') }}</button>
            <button class="nav-link" :class="{ active: isPathActive('/docs') }" :aria-current="isPathActive('/docs') ? 'page' : undefined" @click="navigateTo('Docs', $event)">{{ $t('User guides') }}</button>
          </nav>
          <nav v-else class="nav-links" aria-label="Primary">
            <button
              v-for="item in workspaceNav"
              :key="item.id"
              class="nav-link"
              :class="{ active: isPathActive(item.path) }"
              :aria-current="isPathActive(item.path) ? 'page' : undefined"
              @click="navigateTo(item.route, $event)"
            >{{ $t(item.label) }}</button>

            <!-- Role dashboards (Correctors / Admins / SuperAdmins) grouped under one menu -->
            <div v-if="adminNav.length" class="dd dd-inline" @click.stop v-click-outside="closeMenus">
              <button
                class="nav-link role"
                :class="{ active: adminNav.some(i => isPathActive(i.path)), open: openMenu === 'admin' }"
                :aria-expanded="openMenu === 'admin' ? 'true' : 'false'"
                aria-haspopup="menu"
                @click="toggleMenu('admin')"
              >
                <i class="fas fa-user-shield"></i>
                {{ $t('Admin') }}
                <i class="fas fa-chevron-down nav-caret" aria-hidden="true"></i>
              </button>
              <div v-if="openMenu === 'admin'" class="dd-panel admin-panel" role="menu">
                <div class="dd-section">{{ $t('Administration') }}</div>
                <button
                  v-for="item in adminNav"
                  :key="item.id"
                  class="dd-row"
                  :class="{ 'row-active': isPathActive(item.path) }"
                  role="menuitem"
                  @click="menuNavigate(item.route, $event)"
                >
                  <i :class="['fas', item.icon, 'row-icon', 'row-icon-role']"></i>
                  <span class="row-label">{{ $t(item.label) }}</span>
                  <i class="fas fa-chevron-right row-go"></i>
                </button>
              </div>
            </div>
          </nav>

          <!-- Pas 4.2: mounted for visitors too — Docs search is public;
               GlobalSearch itself gates the account-only sources. -->
          <GlobalSearch variant="wide" />

          <div class="bar-right">

            <button
              v-if="isLoggedIn && !isProductionRadio"
              class="credits-chip"
              :class="{ 'credits-low': hasLowBalance, 'credits-empty': !primaryBalance }"
              :title="$t('Click to manage subscriptions')"
              @click="openSubscriptionModal()"
            >
              <i class="fas chip-icon" :class="hasLowBalance ? 'fa-exclamation-triangle' : 'fa-coins'"></i>
              <span v-if="primaryBalance" class="chip-value">{{ primaryBalance.balance }}<span class="chip-denom">{{ primaryBalance.denomination }}</span></span>
              <span v-else class="chip-value">&mdash;</span>
            </button>

            <div v-if="!isProductionRadio" class="dd" @click.stop v-click-outside="closeMenus">
              <button
                class="icon-btn"
                :class="{ open: openMenu === 'about' }"
                :aria-expanded="openMenu === 'about' ? 'true' : 'false'"
                aria-haspopup="menu"
                :title="$t('Help & resources')"
                :aria-label="$t('Help & resources')"
                @click="toggleMenu('about')"
              >
                <i class="fas" :class="isLoggedIn ? 'fa-circle-question' : 'fa-globe'"></i>
              </button>
              <div v-if="openMenu === 'about'" class="dd-panel about-panel" role="menu">
                <template v-if="!isLoggedIn">
                  <div class="dd-section">{{ $t('Language') }}</div>
                  <button
                    v-for="locale in $i18n.availableLocales"
                    :key="locale"
                    class="dd-row"
                    :class="{ 'row-active': $i18n.locale === locale }"
                    role="menuitem"
                    @click="setLocale(locale)"
                  >
                    <span class="row-flag" v-html="getFlag(locale)"></span>
                    <span class="row-label">{{ getLocaleName(locale) }}</span>
                    <i v-if="$i18n.locale === locale" class="fas fa-check row-check"></i>
                  </button>
                  <div class="dd-divider"></div>
                </template>
                <div class="dd-section">{{ $t('Help & resources') }}</div>
                <template v-for="item in helpNav" :key="item.id">
                  <a v-if="item.href" class="dd-row" role="menuitem" :href="item.href" @click="closeMenus()">
                    <i :class="['fas', item.icon, 'row-icon']"></i>
                    <span class="row-label">{{ $t(item.label) }}</span>
                  </a>
                  <button
                    v-else
                    class="dd-row"
                    :class="{ 'row-active': $route.name === item.route }"
                    role="menuitem"
                    @click="menuNavigate(item.route, $event)"
                  >
                    <i :class="['fas', item.icon, 'row-icon']"></i>
                    <span class="row-label">{{ $t(item.label) }}</span>
                  </button>
                </template>
                <template v-if="isLoggedIn">
                  <div class="dd-divider"></div>
                  <button class="dd-row" role="menuitem" @click="closeMenus(); openSearch()">
                    <i class="fas fa-magnifying-glass row-icon"></i>
                    <span class="row-label">{{ $t('Search or jump to...') }}</span>
                    <kbd class="row-kbd">{{ searchShortcut }}</kbd>
                  </button>
                </template>
                <template v-if="!isLoggedIn">
                  <div class="dd-divider"></div>
                  <div class="theme-row"><ThemeToggle /></div>
                </template>
              </div>
            </div>

            <div v-if="isLoggedIn" class="dd" @click.stop v-click-outside="closeMenus">
              <button
                class="avatar-btn"
                :class="{ open: openMenu === 'user' }"
                :aria-expanded="openMenu === 'user' ? 'true' : 'false'"
                aria-haspopup="menu"
                :title="authStore.fullName"
                :aria-label="authStore.fullName || $t('Account')"
                @click="toggleMenu('user')"
              >
                {{ userInitials }}
                <span v-if="hasLowBalance" class="avatar-dot"></span>
              </button>
              <div v-if="openMenu === 'user'" class="dd-panel user-panel" role="menu">
                <div class="user-head">
                  <span class="user-head-avatar">{{ userInitials }}</span>
                  <span class="user-head-meta">
                    <span class="user-head-name">{{ authStore.fullName }}</span>
                    <span v-if="authStore.email" class="user-head-email">{{ authStore.email }}</span>
                  </span>
                </div>
                <div class="dd-divider"></div>

                <div class="dd-section">{{ $t('Account') }}</div>
                <button
                  v-if="!isProductionRadio"
                  class="sub-card"
                  :title="$t('Click to manage subscriptions')"
                  @click="openSubscriptionModal(); closeMenus()"
                >
                  <span class="sub-icon">
                    <i class="fas" :class="hasActiveSubscription ? 'fa-crown' : 'fa-user-circle'"></i>
                  </span>
                  <span class="sub-body">
                    <span class="sub-title">
                      {{ hasActiveSubscription ? $t('Premium Member') : $t('Free Member') }}
                      <i class="fas fa-external-link-alt sub-ext"></i>
                    </span>
                    <template v-if="subscriptionsDetails">
                      <span
                        v-for="(subscription, index) in subscriptionsDetails"
                        v-show="subscription.status === 'active'"
                        :key="index"
                        class="sub-line"
                        :class="{ 'sub-expiring': subscription.isExpiringSoon }"
                      >
                        <span class="sub-plan">{{ subscription.plan?.name?.split('(')[0] }}</span>
                        <span class="sub-days">{{ subscription.daysLeft }} {{ $t('days left') }}</span>
                      </span>
                    </template>
                    <span v-else class="sub-line">
                      {{ $t('No active subscriptions') }}
                      <i class="fas fa-plus sub-ext"></i>
                    </span>
                    <span class="sub-hint">{{ $t('Click to manage') }}</span>
                  </span>
                </button>
                <div v-if="hasExpiringSubscriptions && !isProductionRadio" class="sub-warning">
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>
                    {{ expiringSubscriptionsCount > 1
                      ? $t('Multiple subscriptions will expire soon')
                      : $t('Your subscription will expire soon') }}
                  </span>
                </div>
                <button
                  v-for="item in accountNav"
                  :key="item.id"
                  class="dd-row"
                  :class="{ 'row-active': isPathActive(item.path) }"
                  role="menuitem"
                  @click="menuNavigate(item.route, $event)"
                >
                  <i :class="['fas', item.icon, 'row-icon']"></i>
                  <span class="row-label">{{ $t(item.label) }}</span>
                </button>
                <button class="dd-row storage-row" role="menuitem" :title="$t('Recount used space')" :aria-busy="storageRecounting ? 'true' : 'false'" @click.stop="refreshDataSize">
                  <i class="fas fa-database row-icon"></i>
                  <span class="row-label row-label-stack">
                    <span>{{ dataSize > 0 ? $t('Used space') : $t('Unknown used space') }}</span>
                    <span v-if="storageBreakdownText" class="row-sub">{{ storageBreakdownText }}</span>
                    <span v-if="storageUpdatedText" class="row-sub">{{ storageUpdatedText }}</span>
                  </span>
                  <span v-if="dataSize > 0" class="row-meta">{{ formattedDataSize }}</span>
                  <i class="fas fa-refresh row-refresh" :class="{ 'fa-spin': storageRecounting }"></i>
                </button>
                <button v-if="storageEstimatedCredits > 0 && !isProductionRadio" class="dd-row storage-cost-row" role="menuitem" @click="openSubscriptionModal('billing'); closeMenus()">
                  <i class="fas fa-coins row-icon"></i>
                  <span class="row-label">{{ $t('Storage: ~{credits} credits/month', { credits: storageEstimatedCredits }) }}</span>
                  <i class="fas fa-chevron-right row-go"></i>
                </button>

                <div class="dd-divider"></div>
                <div class="dd-section">{{ $t('Preferences') }}</div>
                <button
                  class="dd-row"
                  role="menuitem"
                  :aria-expanded="langOpen ? 'true' : 'false'"
                  @click.stop="langOpen = !langOpen"
                >
                  <i class="fas fa-language row-icon"></i>
                  <span class="row-label">{{ $t('Language') }}</span>
                  <span class="row-meta"><span v-html="getFlag($i18n.locale)"></span> {{ getLocaleName($i18n.locale) }}</span>
                  <i class="fas fa-chevron-down row-go row-caret" :class="{ flipped: langOpen }"></i>
                </button>
                <div v-if="langOpen" class="lang-list">
                  <button
                    v-for="locale in $i18n.availableLocales"
                    :key="locale"
                    class="dd-row"
                    :class="{ 'row-active': $i18n.locale === locale }"
                    role="menuitemradio"
                    :aria-checked="$i18n.locale === locale ? 'true' : 'false'"
                    @click="setLocale(locale)"
                  >
                    <span class="row-flag" v-html="getFlag(locale)"></span>
                    <span class="row-label">{{ getLocaleName(locale) }}</span>
                    <i v-if="$i18n.locale === locale" class="fas fa-check row-check"></i>
                  </button>
                </div>
                <div class="theme-row"><ThemeToggle /></div>

                <div class="dd-divider"></div>
                <button class="dd-row row-danger" role="menuitem" @click="goLogin()">
                  <i class="fas fa-sign-out-alt row-icon"></i>
                  <span class="row-label">{{ $t('Logout') }}</span>
                </button>
              </div>
            </div>

            <button v-else class="login-btn" @click="navigateTo('Login', $event)">
              <i class="fas fa-sign-in-alt"></i>
              <span>{{ $t('Login') }}</span>
            </button>

          </div>
        </div>
      </header>

      <template v-else>

        <header class="mobile-top fixed top-0 left-0 right-0 h-14 z-40">
          <button class="brand brand-compact" @click="navigateTo('Home', $event)" :title="$t('Home')">
            <span class="brand-mark" aria-hidden="true">
              <span class="bm-bar"></span><span class="bm-bar"></span><span class="bm-bar"></span>
            </span>
            <span class="brand-word">Transcriptor</span>
          </button>
          <GlobalSearch ref="mobileSearchRef" class="mobile-search" />
          <button
            v-if="isLoggedIn && !isProductionRadio"
            class="credits-chip chip-icon-only"
            :class="{ 'credits-low': hasLowBalance, 'credits-empty': !primaryBalance }"
            :title="$t('Click to manage subscriptions')"
            :aria-label="$t('Credits')"
            @click="openSubscriptionModal()"
          >
            <i class="fas chip-icon" :class="hasLowBalance ? 'fa-exclamation-triangle' : 'fa-coins'"></i>
            <span v-if="primaryBalance" class="chip-value chip-value-mobile">{{ primaryBalance.balance }}</span>
          </button>
          <button
            v-if="isLoggedIn && !shouldUseDefaults"
            class="icon-btn"
            :class="{ open: wpsDrawerOpen }"
            :aria-expanded="wpsDrawerOpen ? 'true' : 'false'"
            :title="dashboardStore.selectedProject?.name || dashboardStore.selectedWorkspace?.name || $t('Workspace')"
            :aria-label="$t('Workspace')"
            @click="wpsDrawerOpen = true"
          >
            <i class="fas fa-briefcase" aria-hidden="true"></i>
          </button>
          <button
            v-if="isLoggedIn"
            class="icon-btn"
            :class="{ open: isPathActive('/Users/assets') }"
            :title="$t('Assets')"
            :aria-label="$t('Assets')"
            @click="navigateTo('AssetsBrowser', $event)"
          >
            <i class="fas fa-folder-open" aria-hidden="true"></i>
          </button>
          <button
            v-if="isLoggedIn && !isProductionRadio"
            class="icon-btn"
            :class="{ open: isPathActive('/Users/videodashboard') }"
            :title="$t('Video')"
            :aria-label="$t('Video')"
            @click="navigateTo('VideoGeneratorDashboard', $event)"
          >
            <i class="fas fa-video" aria-hidden="true"></i>
          </button>
          <button v-if="!isLoggedIn" class="login-btn login-btn-compact" @click="navigateTo('Login', $event)">
            {{ $t('Login') }}
          </button>
        </header>

        <nav class="mobile-bottom fixed bottom-0 left-0 right-0 z-40" aria-label="Primary">
          <button class="tab tab-pinned" :class="{ active: isPathActive('/Users/dashboard') }" :aria-current="isPathActive('/Users/dashboard') ? 'page' : undefined" @click="navigateTo('UserDashboard', $event)">
            <i class="fas fa-gauge-high"></i>
            <span>{{ $t('Dashboard') }}</span>
          </button>
          <div class="tabs-scroll">
            <button class="tab" :class="{ active: isPathActive('/Users/transcriptordashboard') }" :aria-current="isPathActive('/Users/transcriptordashboard') ? 'page' : undefined" @click="navigateTo('TranscriptEditorDashboard', $event)">
              <i class="fas fa-microphone"></i>
              <span>{{ $t('Transcribe') }}</span>
            </button>
            <button class="tab" :class="{ active: isPathActive('/Users/transcripts') }" @click="navigateTo('SavedTranscripts', $event)"><i class="fas fa-file-alt"></i><span>{{ $t('Transcripts') }}</span></button>
            <button class="tab" :class="{ active: isPathActive('/Users/assets') }" :aria-current="isPathActive('/Users/assets') ? 'page' : undefined" @click="navigateTo('AssetsBrowser', $event)">
              <i class="fas fa-folder-open"></i>
              <span>{{ $t('Assets') }}</span>
            </button>
            <button v-if="!isProductionRadio" class="tab" :class="{ active: isPathActive('/Users/videodashboard') }" :aria-current="isPathActive('/Users/videodashboard') ? 'page' : undefined" @click="navigateTo('VideoGeneratorDashboard', $event)">
              <i class="fas fa-video"></i>
              <span>{{ $t('Video') }}</span>
            </button>
            <button v-if="isLoggedIn" class="tab" :aria-label="$t('Search')" @click="openMobileSearch">
              <i class="fas fa-search" aria-hidden="true"></i>
              <span>{{ $t('Search') }}</span>
            </button>
          </div>
          <button class="tab tab-pinned" :class="{ active: mobileSheetOpen }" :aria-expanded="mobileSheetOpen ? 'true' : 'false'" :aria-label="$t('Menu')" aria-haspopup="dialog" @click="mobileSheetOpen = true">
            <i class="fas fa-bars"></i>
            <span>{{ $t('Menu') }}</span>
          </button>
        </nav>

        <Teleport to="body">
        <Transition name="sheet">
          <div v-if="mobileSheetOpen" class="sheet-backdrop" :class="{ dark: isDarkMode }" @click.self="mobileSheetOpen = false">
            <div class="sheet" role="dialog" aria-modal="true" :aria-label="$t('Menu')" tabindex="-1" ref="sheetRef">
              <div class="sheet-handle" aria-hidden="true"></div>
              <div class="sheet-scroll">

                <template v-if="isLoggedIn">
                  <div class="sheet-user">
                    <span class="sheet-avatar">{{ userInitials }}</span>
                    <span class="sheet-user-meta">
                      <span class="sheet-user-name">{{ authStore.fullName }}</span>
                      <span v-if="authStore.email" class="sheet-user-email">{{ authStore.email }}</span>
                    </span>
                  </div>

                  <div class="dd-section">{{ $t('Account') }}</div>
                  <button
                    v-if="!isProductionRadio"
                    class="sub-card"
                    :title="$t('Click to manage subscriptions')"
                    @click="openSubscriptionModal(); mobileSheetOpen = false"
                  >
                    <span class="sub-icon">
                      <i class="fas" :class="hasActiveSubscription ? 'fa-crown' : 'fa-user-circle'"></i>
                    </span>
                    <span class="sub-body">
                      <span class="sub-title">
                        {{ hasActiveSubscription ? $t('Premium Member') : $t('Free Member') }}
                        <i class="fas fa-external-link-alt sub-ext"></i>
                      </span>
                      <template v-if="subscriptionsDetails">
                        <span
                          v-for="(subscription, index) in subscriptionsDetails"
                          v-show="subscription.status === 'active'"
                          :key="index"
                          class="sub-line"
                          :class="{ 'sub-expiring': subscription.isExpiringSoon }"
                        >
                          <span class="sub-plan">{{ subscription.plan?.name?.split('(')[0] }}</span>
                          <span class="sub-days">{{ subscription.daysLeft }} {{ $t('days left') }}</span>
                        </span>
                      </template>
                      <span v-else class="sub-line">
                        {{ $t('No active subscriptions') }}
                        <i class="fas fa-plus sub-ext"></i>
                      </span>
                      <span class="sub-hint">{{ $t('Click to manage') }}</span>
                    </span>
                  </button>
                  <div v-if="hasExpiringSubscriptions && !isProductionRadio" class="sub-warning">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>
                      {{ expiringSubscriptionsCount > 1
                        ? $t('Multiple subscriptions will expire soon')
                        : $t('Your subscription will expire soon') }}
                    </span>
                  </div>
                  <button
                    v-for="item in accountNav"
                    :key="item.id"
                    class="dd-row"
                    :class="{ 'row-active': isPathActive(item.path) }"
                    :aria-current="isPathActive(item.path) ? 'page' : undefined"
                    @click="sheetNavigate(item.route, $event)"
                  >
                    <i :class="['fas', item.icon, 'row-icon']"></i>
                    <span class="row-label">{{ $t(item.label) }}</span>
                  </button>
                  <button class="dd-row storage-row" :title="$t('Recount used space')" :aria-busy="storageRecounting ? 'true' : 'false'" @click.stop="refreshDataSize">
                    <i class="fas fa-database row-icon"></i>
                    <span class="row-label row-label-stack">
                      <span>{{ dataSize > 0 ? $t('Used space') : $t('Unknown used space') }}</span>
                      <span v-if="storageBreakdownText" class="row-sub">{{ storageBreakdownText }}</span>
                      <span v-if="storageUpdatedText" class="row-sub">{{ storageUpdatedText }}</span>
                    </span>
                    <span v-if="dataSize > 0" class="row-meta">{{ formattedDataSize }}</span>
                    <i class="fas fa-refresh row-refresh" :class="{ 'fa-spin': storageRecounting }"></i>
                  </button>
                  <button v-if="storageEstimatedCredits > 0 && !isProductionRadio" class="dd-row storage-cost-row" @click="openSubscriptionModal('billing'); mobileSheetOpen = false">
                    <i class="fas fa-coins row-icon"></i>
                    <span class="row-label">{{ $t('Storage: ~{credits} credits/month', { credits: storageEstimatedCredits }) }}</span>
                    <i class="fas fa-chevron-right row-go"></i>
                  </button>

                  <!-- Main destinations live in the bottom tab bar; this lists role dashboards -->
                  <template v-if="shouldUseDefaults || adminNav.length">
                    <div class="dd-divider"></div>
                    <div class="dd-section">{{ $t(shouldUseDefaults ? 'Workspace' : 'Administration') }}</div>
                  </template>
                  <button v-if="shouldUseDefaults" class="dd-row" :class="{ 'row-active': isPathActive('/Users/transcriptordashboard') }" :aria-current="isPathActive('/Users/transcriptordashboard') ? 'page' : undefined" @click="navigateToProject(); mobileSheetOpen = false">
                    <i class="fas fa-edit row-icon"></i>
                    <span class="row-label">{{ $t('Transcribe') }}</span>
                  </button>
                  <button
                    v-for="item in adminNav"
                    :key="item.id"
                    class="dd-row"
                    :class="{ 'row-active': isPathActive(item.path) }"
                    :aria-current="isPathActive(item.path) ? 'page' : undefined"
                    @click="sheetNavigate(item.route, $event)"
                  >
                    <i :class="['fas', item.icon, 'row-icon', 'row-icon-role']"></i>
                    <span class="row-label">{{ $t(item.label) }}</span>
                    <i class="fas fa-chevron-right row-go"></i>
                  </button>

                  <div class="dd-divider"></div>
                  <div class="dd-section">{{ $t('Preferences') }}</div>
                  <button class="dd-row" :aria-expanded="langOpen ? 'true' : 'false'" @click.stop="langOpen = !langOpen">
                    <i class="fas fa-language row-icon"></i>
                    <span class="row-label">{{ $t('Language') }}</span>
                    <span class="row-meta"><span v-html="getFlag($i18n.locale)"></span> {{ getLocaleName($i18n.locale) }}</span>
                    <i class="fas fa-chevron-down row-go row-caret" :class="{ flipped: langOpen }"></i>
                  </button>
                  <div v-if="langOpen" class="lang-list">
                    <button
                      v-for="locale in $i18n.availableLocales"
                      :key="locale"
                      class="dd-row"
                      :class="{ 'row-active': $i18n.locale === locale }"
                      @click="setLocale(locale)"
                    >
                      <span class="row-flag" v-html="getFlag(locale)"></span>
                      <span class="row-label">{{ getLocaleName(locale) }}</span>
                      <i v-if="$i18n.locale === locale" class="fas fa-check row-check"></i>
                    </button>
                  </div>
                  <div class="theme-row"><ThemeToggle /></div>

                  <template v-if="helpNav.length">
                    <div class="dd-divider"></div>
                    <div class="dd-section">{{ $t('Help & resources') }}</div>
                    <template v-for="item in helpNav" :key="item.id">
                      <a v-if="item.href" class="dd-row" :href="item.href" @click="mobileSheetOpen = false">
                        <i :class="['fas', item.icon, 'row-icon']"></i>
                        <span class="row-label">{{ $t(item.label) }}</span>
                      </a>
                      <button
                        v-else
                        class="dd-row"
                        :class="{ 'row-active': $route.name === item.route }"
                        @click="sheetNavigate(item.route, $event)"
                      >
                        <i :class="['fas', item.icon, 'row-icon']"></i>
                        <span class="row-label">{{ $t(item.label) }}</span>
                      </button>
                    </template>
                  </template>

                  <div class="dd-divider"></div>
                  <button class="dd-row row-danger" @click="goLogin()">
                    <i class="fas fa-sign-out-alt row-icon"></i>
                    <span class="row-label">{{ $t('Logout') }}</span>
                  </button>
                </template>

                <template v-else>
                  <button class="login-btn sheet-login" @click="navigateTo('Login', $event)">
                    <i class="fas fa-sign-in-alt"></i>
                    <span>{{ $t('Login') }}</span>
                  </button>

                  <div class="dd-section">{{ $t('Help & resources') }}</div>
                  <template v-for="item in helpNav" :key="item.id">
                    <a v-if="item.href" class="dd-row" :href="item.href" @click="mobileSheetOpen = false">
                      <i :class="['fas', item.icon, 'row-icon']"></i>
                      <span class="row-label">{{ $t(item.label) }}</span>
                    </a>
                    <button
                      v-else
                      class="dd-row"
                      :class="{ 'row-active': $route.name === item.route }"
                      @click="sheetNavigate(item.route, $event)"
                    >
                      <i :class="['fas', item.icon, 'row-icon']"></i>
                      <span class="row-label">{{ $t(item.label) }}</span>
                    </button>
                  </template>

                  <div class="dd-divider"></div>
                  <div class="dd-section">{{ $t('Preferences') }}</div>
                  <button
                    v-for="locale in $i18n.availableLocales"
                    :key="locale"
                    class="dd-row"
                    :class="{ 'row-active': $i18n.locale === locale }"
                    @click="setLocale(locale)"
                  >
                    <span class="row-flag" v-html="getFlag(locale)"></span>
                    <span class="row-label">{{ getLocaleName(locale) }}</span>
                    <i v-if="$i18n.locale === locale" class="fas fa-check row-check"></i>
                  </button>
                  <div class="theme-row"><ThemeToggle /></div>
                </template>

              </div>
            </div>
          </div>
        </Transition>
        </Teleport>

      </template>
    </template>

    <SubscriptionModal v-if="isLoggedIn && !isProductionRadio" ref="subscriptionModal" />

    <Teleport to="body">
      <button
        v-if="isLoggedIn && !shouldUseDefaults && !editor.showSrtEditor.value"
        class="wps-fab"
        :class="{ dragging: wpsDragging }"
        :style="{ top: wpsFabTop + 'px' }"
        :aria-expanded="wpsDrawerOpen ? 'true' : 'false'"
        aria-haspopup="dialog"
        :title="dashboardStore.selectedProject?.name || dashboardStore.selectedWorkspace?.name || $t('Workspace')"
        :aria-label="$t('Workspace')"
        @pointerdown="onWpsFabPointerDown"
      >
        <i class="fas fa-briefcase" aria-hidden="true"></i>
        <span v-if="dashboardStore.selectedProject?.name || dashboardStore.selectedWorkspace?.name" class="wps-fab-dot" aria-hidden="true"></span>
      </button>
    </Teleport>

    <Teleport to="body">
      <button
        v-if="isLoggedIn && !editor.showSrtEditor.value"
        class="wps-fab notif-fab"
        :class="{ dragging: notifDragging }"
        :style="{ top: notifFabTop + 'px' }"
        :aria-expanded="notifDrawerOpen ? 'true' : 'false'"
        aria-haspopup="dialog"
        :title="$t('Notifications')"
        :aria-label="$t('Notifications')"
        @pointerdown="onNotifFabPointerDown"
      >
        <i class="fas fa-bell" aria-hidden="true"></i>
        <span v-if="unreadCount > 0" class="notif-fab-badge" aria-hidden="true">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </button>
    </Teleport>

    <Teleport to="body">
      <Transition name="wps-drawer">
        <div
          v-if="wpsDrawerOpen && isLoggedIn && !shouldUseDefaults"
          class="wps-drawer-backdrop"
          :class="{ dark: isDarkMode }"
          @click.self="wpsDrawerOpen = false"
        >
          <div
            class="wps-drawer"
            role="dialog"
            aria-modal="true"
            :aria-label="$t('Workspace')"
            ref="wpsDrawerRef"
            tabindex="-1"
          >
            <div class="wps-drawer-head">
              <span class="wps-drawer-title">
                <i class="fas fa-briefcase" aria-hidden="true"></i>
                {{ $t('Workspace') }}
              </span>
              <button class="wps-drawer-close" :aria-label="$t('Close')" @click="wpsDrawerOpen = false">
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div class="wps-drawer-body">
              <WorkspaceProjectSelector mode="drawer" @navigate="wpsDrawerOpen = false" />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="wps-drawer">
        <div
          v-if="notifDrawerOpen && isLoggedIn"
          class="wps-drawer-backdrop"
          :class="{ dark: isDarkMode }"
          @click.self="notifDrawerOpen = false"
        >
          <div
            class="wps-drawer"
            role="dialog"
            aria-modal="true"
            :aria-label="$t('Notifications')"
            ref="notifDrawerRef"
            tabindex="-1"
          >
            <div class="wps-drawer-head">
              <span class="wps-drawer-title">
                <i class="fas fa-bell" aria-hidden="true"></i>
                {{ $t('Notifications') }}
                <span v-if="unreadCount > 0" class="notif-unread-pill" aria-hidden="true">{{ unreadCount }}</span>
              </span>
              <span class="notif-head-actions">
                <button v-if="notifCenter.sortedItems.length > 0" class="notif-clear" @click="notifCenter.clearAll()">
                  {{ $t('Clear all') }}
                </button>
                <button class="wps-drawer-close" :aria-label="$t('Close')" @click="notifDrawerOpen = false">
                  <i class="fas fa-times" aria-hidden="true"></i>
                </button>
              </span>
            </div>
            <div class="wps-drawer-body notif-drawer-body">
              <div v-if="notifCenter.sortedItems.length === 0" class="notif-empty">
                <i class="fas fa-bell-slash" aria-hidden="true"></i>
                <p>{{ $t('No notifications yet') }}</p>
              </div>
              <div v-for="item in notifCenter.sortedItems" :key="item.id" class="notif-card" :class="'notif-' + item.type">
                <span class="notif-icon"><i class="fas" :class="notifIcon(item.type)" aria-hidden="true"></i></span>
                <span class="notif-meta">
                  <span class="notif-text">{{ $t(item.messageKey, item.messageParams || {}) }}</span>
                  <span v-if="item.meta" class="notif-sub">{{ item.meta }}</span>
                </span>
                <span class="notif-actions">
                  <button v-if="item.action" class="notif-review" @click="openNotifAction(item)">
                    {{ $t(item.action.labelKey) }}
                  </button>
                  <button class="notif-dismiss" :aria-label="$t('Dismiss')" @click="notifCenter.dismiss(item.id)">
                    <i class="fas fa-times" aria-hidden="true"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
<script>
import { defineComponent, defineAsyncComponent, ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthUserStore } from '@stores/auth';
import { useSessionStore } from '@stores/session';
import { useSubscriptionStore } from '@stores/subscription';
import { useDashboardStore } from '@stores/dashboardStore';
import { useNotificationCenterStore } from '@stores/notificationCenter';
import { useThemeStore } from '@stores/themeStore';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import AutoService from '@services/AutoService';
import { openWithLoading } from '@utils/openWithLoading';
// Pas 1.1 repair: modal + workspace drawer are interaction-only
// surfaces (rendered behind isLoggedIn / wpsDrawerOpen), so they load
// as lazy chunks instead of taxing every landing visit. Same
// defineAsyncComponent pattern as Homepage's SrtEditor.
const SubscriptionModal = defineAsyncComponent(() => import('@components/SubscriptionModal.vue'));
import GlobalSearch from '@components/GlobalSearch.vue';
const WorkspaceProjectSelector = defineAsyncComponent(() => import('@components/WorkspaceProjectSelector.vue'));
import ThemeToggle from '@components/ThemeToggle.vue';
import DraftSelector from '@components/SrtEditor/DraftSelector.vue';
import { useTranscriptEditor } from '@components/SrtEditor/useTranscriptEditor';
import clickOutside from '../directives/click-outside';
import { buildNavCatalog, itemsInSection } from '@components/navigation/navCatalog';
import { formatRelativeTime, formatBytes } from '@utils/retention';
import { normalizeStorageBilling, estimatedMonthlyCredits } from '@utils/storageBilling';
import { setupLocale } from '../i18nlocal.js';

export default defineComponent({
    name: 'Navigation',
    components: {
        SubscriptionModal,
        GlobalSearch,
        WorkspaceProjectSelector,
        ThemeToggle,
        DraftSelector
    },
    directives: {
        clickOutside
    },
    setup() {
      const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
      const onResize = () => { windowWidth.value = window.innerWidth; };
      const isDesktop = computed(() => !__IS_NATIVE__ && windowWidth.value >= 1024);

      const dashboardStore = useDashboardStore();
      const notifCenter = useNotificationCenterStore();
      const subscriptionStore = useSubscriptionStore();
      const authStore = useAuthUserStore();
      const themeStore = useThemeStore();
      const { isDarkMode } = storeToRefs(themeStore);
      const editor = useTranscriptEditor();
      const currentMode = import.meta.env.MODE;
      const router = useRouter();
      const route = useRoute();
      const { t, locale } = useI18n();

      const { shouldUseDefaults } = storeToRefs(dashboardStore);

      const userBalances = ref([]);
      const dataSize = ref(0);
      // UsersTable METADATA also carries a per-prefix breakdown and the time
      // of the last authoritative S3 recount (storage reconciliation, W4).
      const storageBreakdown = ref(null);
      const storageReconciledAt = ref('');
      const storageRecounting = ref(false);
      // Projected storage charge this month (GET /storage/billing, W5). 0 hides
      // the hint; the endpoint may not be deployed yet (404) — stay silent.
      const storageEstimatedCredits = ref(0);

      const openMenu = ref(null);
      const langOpen = ref(false);
      const mobileSheetOpen = ref(false);
      const mobileSearchRef = ref(null);
      const sheetRef = ref(null);
      const wpsDrawerOpen = ref(false);
      const wpsDrawerRef = ref(null);
      const wpsDragging = ref(false);
      const wpsFabTop = ref(Number(localStorage.getItem('wpsFabTop')) || 240);
      const notifDrawerOpen = ref(false);
      const notifDrawerRef = ref(null);
      const notifDragging = ref(false);
      const notifFabTop = ref(Number(localStorage.getItem('notifFabTop')) || 170);
      const unreadCount = computed(() => notifCenter.unreadCount);

      const onWpsFabPointerDown = (e) => {
        const startY = e.clientY;
        const startTop = wpsFabTop.value;
        let moved = false;
        wpsDragging.value = false;
        const el = e.currentTarget;
        el.setPointerCapture?.(e.pointerId);
        const onMove = (ev) => {
          const dy = ev.clientY - startY;
          if (Math.abs(dy) > 5) { moved = true; wpsDragging.value = true; }
          if (moved) {
            const max = Math.max(80, window.innerHeight - 80);
            wpsFabTop.value = Math.min(max, Math.max(80, startTop + dy));
          }
        };
        const onUp = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
          if (moved) {
            localStorage.setItem('wpsFabTop', String(Math.round(wpsFabTop.value)));
          } else {
            wpsDrawerOpen.value = !wpsDrawerOpen.value;
          }
          wpsDragging.value = false;
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
      };

      const closeMenus = () => { openMenu.value = null; langOpen.value = false; };
      const toggleMenu = (name) => { langOpen.value = false; openMenu.value = openMenu.value === name ? null : name; };

      const onNotifFabPointerDown = (e) => {
        const startY = e.clientY;
        const startTop = notifFabTop.value;
        let moved = false;
        notifDragging.value = false;
        const el = e.currentTarget;
        el.setPointerCapture?.(e.pointerId);
        const onMove = (ev) => {
          const dy = ev.clientY - startY;
          if (Math.abs(dy) > 5) { moved = true; notifDragging.value = true; }
          if (moved) {
            const max = Math.max(80, window.innerHeight - 80);
            notifFabTop.value = Math.min(max, Math.max(80, startTop + dy));
          }
        };
        const onUp = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
          if (moved) {
            localStorage.setItem('notifFabTop', String(Math.round(notifFabTop.value)));
          } else {
            notifDrawerOpen.value = !notifDrawerOpen.value;
            if (notifDrawerOpen.value) notifCenter.markAllRead();
          }
          notifDragging.value = false;
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
      };

      const notifIcon = (type) => {
        switch (type) {
          case 'warning': return 'fa-hourglass-half';
          case 'danger': return 'fa-exclamation-triangle';
          case 'success': return 'fa-check-circle';
          default: return 'fa-circle-info';
        }
      };

      const openNotifAction = (item) => {
        notifDrawerOpen.value = false;
        const target = item?.action?.route;
        if (!target) return;
        router.push({ name: target.name, query: target.query }).catch(err => {
          console.error('Navigation failed:', err);
        });
      };
      const openMobileSearch = () => {
        const search = mobileSearchRef.value;
        if (search && typeof search.open === 'function') {
          search.open();
        }
      };

      const trapFocusInSheet = (e) => {
        if (!mobileSheetOpen.value || !sheetRef.value) return;
        if (e.key !== 'Tab') return;
        const focusable = sheetRef.value.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      const primaryBalance = computed(() => {
        return userBalances.value && userBalances.value.length > 0 ? userBalances.value[0] : null;
      });

      const userInitials = computed(() => {
        const parts = String(authStore.fullName || 'U').trim().split(/\s+/);
        const initials = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
        return initials || 'U';
      });

      const subscriptionStatus = computed(() => {
        if (!authStore.isAuthenticated) return 'not_logged_in';
        const subscriptions = subscriptionStore.currentSubscriptions;
        if (!subscriptions || subscriptions.length === 0) return 'free';
        if (subscriptions.some(sub => sub.status === 'past_due')) return 'past_due';
        if (subscriptions.some(sub => sub.status === 'active')) return 'active';
        return 'inactive';
      });

      const hasActiveSubscription = computed(() => {
        return subscriptionStatus.value === 'active';
      });

      const subscriptionsDetails = computed(() => {
        let subscriptions = subscriptionStore.enhancedSubscriptions;
        if (!subscriptions || subscriptions.length === 0) {
          return null;
        }
        return subscriptions.map(subscription => ({
          plan: subscription.plan,
          daysLeft: subscription.daysUntilExpiration,
          isExpiringSoon: subscription.isExpiringSoon,
          status: subscription.status
        }));
      });

      const hasExpiringSubscriptions = computed(() => {
        return subscriptionsDetails.value?.some(sub => sub.isExpiringSoon) || false;
      });

      const expiringSubscriptionsCount = computed(() => {
        return subscriptionsDetails.value?.filter(sub => sub.isExpiringSoon).length || 0;
      });

      const subscriptionModal = ref(null);

      // Accepts a tab name or the 'open-subscription-modal' event
      // (CustomEvent detail { tab }) for deep links such as the storage hint.
      const openSubscriptionModal = (arg) => {
        const tab = typeof arg === 'string' ? arg : arg?.detail?.tab;
        // Async chunk may still be loading on a very early click —
        // no-op then (same tolerance as the radio-mode v-if case).
        subscriptionModal.value?.openModal?.(tab);
      };

      const supportEmail = computed(() =>
        useSessionStore().appConfig?.contactEmail || 'office@transcriptor.ro'
      );
      const searchShortcut = typeof navigator !== 'undefined'
        && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent) ? '⌘K' : 'Ctrl+K';
      // GlobalSearch listens for this event, whichever instance is mounted.
      const openSearch = () => window.dispatchEvent(new Event('open-global-search'));

      // Mount + auth watcher fire these concurrently at boot — coalesce.
      let dataSizeInflight = false;
      let balancesInflight = false;
      const fetchDataSize = async () => {
        if (dataSizeInflight) return;
        dataSizeInflight = true;
        dataSize.value = 0;
        try {
          const response = await AutoService.fetchOrder('user');
          if (response.Item && response.Item.size) {
            dataSize.value = response.Item.size;
          } else {
            dataSize.value = 0;
          }
          storageBreakdown.value = response?.Item?.storageBreakdown || null;
          storageReconciledAt.value = response?.Item?.storageReconciledAt || '';
        } catch (error) {
          console.error('Error fetching data size:', error);
          dataSize.value = 0;
        } finally {
          dataSizeInflight = false;
        }
        fetchStorageEstimate();
      };

      const fetchStorageEstimate = async () => {
        try {
          const billing = normalizeStorageBilling(await AutoService.fetchOrder('storage/billing'));
          storageEstimatedCredits.value = estimatedMonthlyCredits(billing);
        } catch {
          storageEstimatedCredits.value = 0;
        }
      };

      // Refresh = ask the backend to recount from S3 (POST /storage/recount),
      // then re-read the stored counter. A 429 means a recount ran in the
      // last 10 minutes: the stored value is already fresh, just re-read.
      const refreshDataSize = async () => {
        if (storageRecounting.value) return;
        storageRecounting.value = true;
        try {
          await AutoService.post('storage/recount');
        } catch (error) {
          if (error?.response?.status !== 429) console.warn('Storage recount failed:', error);
        } finally {
          storageRecounting.value = false;
        }
        await fetchDataSize();
      };

      const storageBreakdownText = computed(() => {
        const b = storageBreakdown.value;
        if (!b || typeof b !== 'object') return '';
        return t('Uploads {files} · Transcripts {transcripts} · Drafts {drafts}', {
          files: formatBytes(Number(b.files) || 0),
          transcripts: formatBytes(Number(b.transcripts) || 0),
          drafts: formatBytes(Number(b.drafts) || 0),
        });
      });
      const storageUpdatedText = computed(() => {
        const when = formatRelativeTime(storageReconciledAt.value, String(locale.value));
        return when ? t('Updated {when}', { when }) : '';
      });

      const fetchUserBalances = async () => {
        if (balancesInflight) return;
        balancesInflight = true;
        userBalances.value = [];
        try {
          const response = await AutoService.fetchOrder('subscription/credits');
          if (!response || !response.Item) {
            userBalances.value = [];
            return;
          }

          userBalances.value = [ { balance: response.Item.balance, denomination: response.Item.denomination } ]

        } catch (error) {
          console.error('Error fetching user balances:', error);
          userBalances.value = [];
        } finally {
          balancesInflight = false;
        }
      };

      const goLogin = () => {
        authStore.logout();
        router.push({ name: 'Login' });
      };

      const navigateToProject = () => openWithLoading(router, async () => {
        // Transcribe entry point: with no workspace/project selected (e.g.
        // fresh login), fall back to the default workspace + project instead
        // of landing on a blank picker. Otherwise resume where left off.
        try {
          await dashboardStore.ensureDefaultSelection();
        } catch (err) {
          console.error('Default selection failed:', err);
        }
        const wsId = dashboardStore.selectedWorkspaceId;
        const projId = dashboardStore.selectedProjectId;
        const query = wsId && projId ? { workspace: wsId, project: projId } : undefined;
        return { name: 'TranscriptEditorDashboard', query };
      });

      const closeSrtEditor = () => {
        editor.closeSrtEditor();
      };

      const onKeydown = (event) => {
        if (event.key === 'Escape') {
          openMenu.value = null;
          mobileSheetOpen.value = false;
          wpsDrawerOpen.value = false;
          notifDrawerOpen.value = false;
        }
        trapFocusInSheet(event);
        trapFocusInWpsDrawer(event);
        trapFocusInNotifDrawer(event);
      };

      const trapFocusInWpsDrawer = (e) => {
        if (!wpsDrawerOpen.value || !wpsDrawerRef.value) return;
        if (e.key !== 'Tab') return;
        const focusable = wpsDrawerRef.value.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      const trapFocusInNotifDrawer = (e) => {
        if (!notifDrawerOpen.value || !notifDrawerRef.value) return;
        if (e.key !== 'Tab') return;
        const focusable = notifDrawerRef.value.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      onMounted(() => {
        window.addEventListener('resize', onResize);
        window.addEventListener("open-subscription-modal", openSubscriptionModal);
        document.addEventListener('keydown', onKeydown);
        dashboardStore.refreshWorkspaces({ silent: true });
        fetchUserBalances();
        fetchDataSize();
      });

      onUnmounted(() => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener("open-subscription-modal", openSubscriptionModal);
        document.removeEventListener('keydown', onKeydown);
      });

      watch(mobileSheetOpen, async (open) => {
        if (!open) langOpen.value = false;
        if (open) {
          await nextTick();
          const firstFocusable = sheetRef.value?.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          firstFocusable?.focus();
        }
      });

      watch(
        () => authStore.isAuthenticated,
        (newValue) => {
          if (newValue) {
            dashboardStore.refreshWorkspaces({ silent: true });
            fetchUserBalances();
            fetchDataSize();
          } else {
            userBalances.value = [];
            dataSize.value = 0;
            openMenu.value = null;
            mobileSheetOpen.value = false;
            wpsDrawerOpen.value = false;
            notifDrawerOpen.value = false;
          }
        }
      );

      watch(
        () => route.path,
        () => {
          openMenu.value = null;
          mobileSheetOpen.value = false;
          wpsDrawerOpen.value = false;
          notifDrawerOpen.value = false;
        }
      );

      return {
        isDarkMode,
        isDesktop,
        editor,
        currentMode,
        authStore,
        dashboardStore,
        shouldUseDefaults,
        currentTranscriptSource: editor.currentTranscriptSource,
        userBalances,
        dataSize,
        primaryBalance,
        userInitials,
        openMenu,
        mobileSheetOpen,
        mobileSearchRef,
        sheetRef,
        wpsDrawerOpen,
        wpsDrawerRef,
        wpsDragging,
        wpsFabTop,
        onWpsFabPointerDown,
        notifCenter,
        notifDrawerOpen,
        notifDrawerRef,
        notifDragging,
        notifFabTop,
        unreadCount,
        onNotifFabPointerDown,
        notifIcon,
        openNotifAction,
        supportEmail,
        closeMenus,
        toggleMenu,
        openMobileSearch,
        trapFocusInSheet,
        trapFocusInWpsDrawer,
        trapFocusInNotifDrawer,
        subscriptionsDetails,
        hasActiveSubscription,
        hasExpiringSubscriptions,
        expiringSubscriptionsCount,
        subscriptionModal,
        openSubscriptionModal,
        langOpen,
        searchShortcut,
        openSearch,
        fetchDataSize,
        refreshDataSize,
        storageRecounting,
        storageBreakdownText,
        storageUpdatedText,
        storageEstimatedCredits,
        goLogin,
        navigateToProject,
        closeSrtEditor
      };
    },
    computed: {
      dashboardRoutes() {
        if (!this.$router?.options?.routes) return [];

        return this.$router.options.routes
          .filter(route => {
            if (route.meta?.requires && this.authStore.groups) {
              return this.authStore.groups.includes(route.meta.requires);
            }
            return false;
          });
      },

      roleRoutes() {
        return this.dashboardRoutes.filter(route => route.meta?.requires !== 'Users');
      },

      navCatalog() {
        return buildNavCatalog({
          isLoggedIn: this.isLoggedIn,
          isProductionRadio: this.isProductionRadio,
          roleRoutes: this.roleRoutes,
          supportEmail: this.supportEmail,
        });
      },
      workspaceNav() { return itemsInSection(this.navCatalog, 'workspace'); },
      // Subscription has its own card in the account menus
      accountNav() { return itemsInSection(this.navCatalog, 'account').filter(i => !i.action); },
      adminNav() { return itemsInSection(this.navCatalog, 'admin'); },
      helpNav() { return itemsInSection(this.navCatalog, 'help'); },

      isLoggedIn() {
        return useAuthUserStore().isAuthenticated;
      },

      isProductionRadio() {
        return this.currentMode === 'productionradio';
      },

      formattedDataSize() {
        return formatBytes(this.dataSize);
      },

      hasLowBalance() {
        return this.userBalances && this.userBalances.length > 0 &&
               this.userBalances[0].balance < 50;
      },
    },
    methods: {
      async handleDraftSelected(versionId) {
        if (this.currentTranscriptSource && this.currentTranscriptSource.sourceId) {
          if (versionId === 'original') {
            await this.editor.loadOriginalContent();
          } else {
            await this.editor.selectDraft(this.currentTranscriptSource.sourceId, versionId);
          }
        }
      },
      async handleDraftCreated(versionId) {
        if (this.currentTranscriptSource && this.currentTranscriptSource.sourceId) {
          await this.editor.selectDraft(this.currentTranscriptSource.sourceId, versionId);
        }
      },
      async handleDraftDeleted(versionId) {
        if (this.currentTranscriptSource && this.currentTranscriptSource.sourceId) {
          await this.editor.fetchDrafts(this.currentTranscriptSource.sourceId);
        }
      },
      navigateTo(target, event) {
        // Accepts a route name or a path: some role routes (e.g. /Correctors)
        // are registered without a name, only a redirect path.
        if (!target) return;
        const isPath = String(target).startsWith('/');
        if (isPath) {
          if (this.$route.path !== target) {
            this.$router.push({ path: target }).catch((err) => { void err; });
          }
          return;
        }
        if (this.$route.name !== target) {
          this.$router.push({ name: target }).catch((err) => { void err; });
        }
      },
      menuNavigate(routeName, event) {
        this.closeMenus();
        this.navigateTo(routeName, event);
      },
      sheetNavigate(routeName, event) {
        this.mobileSheetOpen = false;
        this.navigateTo(routeName, event);
      },
      isPathActive(path) {
        const currentPath = this.$route.path;
        return currentPath === path || currentPath.startsWith(path + '/');
      },
      getFlag(locale) {
        const flags = {
          'en': '🇺🇸',
          'fr': '🇫🇷',
          'ro': '🇷🇴',
          'gr': '🇬🇷',
          'cn': '🇨🇳',
          'ar': '🇸🇦',
          'it': '🇮🇹',
          'de': '🇩🇪'
        };

        return flags[locale] || '';
      },
      getLocaleName(locale) {
        if (locale == 'en') {
          return 'English';
        }
        else if (locale == 'fr') {
          return 'French';
        }
        else if (locale == 'ro') {
          return 'Romanian';
        }
        else if (locale == 'gr') {
          return 'Greek';
        }
        else if (locale == 'cn') {
          return 'Chinese';
        }
        else if (locale == 'ar') {
          return 'Arabic';
        }
        else if (locale == 'it') {
          return 'Italian';
        }
        else if (locale == 'de') {
          return 'German';
        }
        return locale;
      },
      async setLocale(locale) {
        // Pas 1.1: non-ro messages live in lazy chunks — load before
        // switching, otherwise the UI falls back to ro strings.
        await setupLocale(locale);
        useSessionStore().setLocale(locale);
        if (this.$route.path === '/authentication') {
          await this.$router.go();
        }
      }
    }
});
</script>
<style scoped>
/* Canonical tokens — mapped to --adm-* / --bg-* / --text-* with fallbacks (visual appearance preserved) */
.main-navbar {
  --ink: var(--adm-text-strong, #0a0f2c);
  --ink-2: var(--adm-text-strong, #131a3d);
  --brand: var(--adm-accent, #003cff);
  --brand-soft: var(--adm-accent-soft, #e6ecff);
  --signal: var(--adm-success, #00e5a0);
  --amber: var(--adm-warning, #ffb800);
  --paper: var(--adm-bg, #f5f7fc);
  --body-text: var(--adm-text, #3a4160);
  --muted: var(--adm-muted, #8a92b8);
  --display-font: var(--adm-font-display, 'Sora', sans-serif);
  --body-font: var(--font-body, 'IBM Plex Sans', sans-serif);
  --mono-font: var(--adm-font-mono, 'IBM Plex Mono', monospace);
  --bar-bg: var(--adm-card, #ffffff);
  --panel: var(--adm-card, #ffffff);
  --line: var(--adm-border, #e4e8f5);
  --hover: var(--adm-card-head, #f2f4fb);
  --text-strong: var(--adm-text-strong, #0a0f2c);
  --danger: var(--adm-danger, #d92d20);
  --warn-bg: var(--adm-warning-soft, #fff8e6);
  --warn-line: rgba(255, 184, 0, 0.55);
  --warn-text: var(--adm-warning, #8a5b00);
  --shadow-panel: var(--adm-shadow, 0 16px 40px rgba(10, 15, 44, 0.16));
  font-family: var(--body-font);
  /* Stack above homepage hero (console-window has transform which creates its own stacking context) */
  z-index: 100;
}
.main-navbar.dark,
html.dark-mode .main-navbar {
  --bar-bg: var(--adm-card, #101636);
  --panel: var(--adm-card, #141b3f);
  --line: var(--adm-border, #26305e);
  --hover: var(--adm-card-head, #1b2250);
  --text-strong: var(--adm-text-strong, #e6e9f7);
  --body-text: var(--adm-text, #c3c9e2);
  --brand-soft: var(--adm-accent-soft, #1a2250);
  --warn-bg: var(--adm-warning-soft, rgba(255, 184, 0, 0.12));
  --warn-text: var(--adm-warning, #ffd35c);
  --shadow-panel: var(--adm-shadow, 0 16px 40px rgba(0, 0, 0, 0.5));
}
.main-navbar :focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
  border-radius: 6px;
}

/* ---------- Brand ---------- */
.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 8px;
  transition: background 0.15s ease;
  flex-shrink: 0;
}
.brand:hover { background: var(--hover); }
.brand-mark {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--ink);
  border: 1px solid transparent;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
  padding-bottom: 7px;
  flex-shrink: 0;
  transition: transform 0.18s ease;
}
.main-navbar.dark .brand-mark,
html.dark-mode .main-navbar .brand-mark { border-color: var(--line); }
.bm-bar {
  width: 3px;
  border-radius: 2px;
  background: var(--signal);
  transform-origin: bottom;
}
.bm-bar:nth-child(1) { height: 8px; animation-delay: 0s; }
.bm-bar:nth-child(2) { height: 14px; animation-delay: 0.12s; }
.bm-bar:nth-child(3) { height: 10px; animation-delay: 0.24s; }
.brand:hover .brand-mark { transform: translateY(-1px); }
.brand:hover .bm-bar { animation: nav-eq 0.6s ease-in-out infinite alternate; }
@keyframes nav-eq {
  from { transform: scaleY(0.45); }
  to { transform: scaleY(1); }
}
.brand-word {
  font-family: var(--display-font);
  font-weight: 700;
  font-size: 17px;
  letter-spacing: -0.02em;
  color: var(--text-strong);
  white-space: nowrap;
}

/* ---------- Desktop top bar ---------- */
.topbar {
  background: var(--bar-bg);
  border-bottom: 1px solid var(--line);
  box-shadow: 0 4px 16px rgba(10, 15, 44, 0.05);
}
.bar-inner {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 20px;
}
.nav-links {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 6px;
}
.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  font-family: var(--body-font);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--body-text);
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
}
.nav-link:hover {
  background: var(--hover);
  color: var(--text-strong);
  transform: translateY(-1px);
}
.nav-link.active {
  background: var(--brand-soft);
  color: var(--brand);
  font-weight: 600;
}
.nav-link.role {
  border-color: var(--line);
  background: var(--panel);
  margin-left: 4px;
}
.nav-link.role i { color: var(--brand); font-size: 0.8rem; }
.nav-link.role:hover { border-color: var(--brand); color: var(--brand); }
.nav-link.role.active { border-color: var(--brand); }
.bar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* GlobalSearch trigger, restyled for the product chrome */
.bar-inner :deep(.global-search) { margin: 0 10px; }
.bar-inner :deep(.search-trigger),
.mobile-search :deep(.search-trigger) {
  background: var(--hover);
  border: 1px solid var(--line);
  color: var(--muted);
  border-radius: 8px;
  padding: 7px 12px;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
}
.bar-inner :deep(.search-trigger:hover),
.mobile-search :deep(.search-trigger:hover) {
  background: var(--panel);
  border-color: var(--brand);
  color: var(--brand);
  transform: translateY(-1px);
}
/* Labelled search pill on wide screens; collapses to an icon when the bar gets tight */
.bar-inner :deep(.search-trigger-wide) { height: 36px; gap: 8px; }
@media (max-width: 1279px) {
  .bar-inner :deep(.search-trigger-wide) { width: 36px; padding: 0; justify-content: center; }
  .bar-inner :deep(.search-trigger-wide .trigger-label),
  .bar-inner :deep(.search-trigger-wide .trigger-kbd) { display: none; }
}
/* Workspace/project selector buttons */
.wps-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
}
.wps-wrap :deep(.balance-button) {
  min-height: 0;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--body-text);
  border-radius: 8px;
  font-size: 0.83rem;
  max-width: 150px;
  transition: border-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
}
.wps-wrap :deep(.balance-button:hover) {
  border-color: var(--brand);
  color: var(--brand);
  transform: translateY(-1px);
}
.wps-wrap :deep(.balance-button .truncate) { max-width: 92px; }
.wps-wrap :deep(.balance-button i) { font-size: 0.8rem; }

/* ---------- Credits chip ---------- */
.credits-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--text-strong);
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}
.credits-chip .chip-icon { color: var(--amber); font-size: 12.5px; }
.credits-chip:hover {
  border-color: var(--brand);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 60, 255, 0.12);
}
.chip-value {
  font-family: var(--mono-font);
  font-size: 12px;
  font-weight: 500;
}
.chip-denom {
  color: var(--muted);
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-left: 3px;
}
.credits-low {
  background: var(--warn-bg);
  border-color: var(--warn-line);
  color: var(--warn-text);
}
.credits-low .chip-icon { color: var(--warn-text); }
.credits-low:hover {
  border-color: var(--amber);
  box-shadow: 0 4px 12px rgba(255, 184, 0, 0.18);
}
.credits-empty { color: var(--muted); }
.chip-icon-only { padding: 0 11px; }

/* ---------- Dropdowns ---------- */
.dd { position: relative; }
/* Admin dropdown sits in the left-aligned primary nav, so it opens rightwards */
.dd-inline { display: inline-flex; }
.admin-panel { left: 0; right: auto; width: 260px; transform-origin: top left; }
.nav-caret { font-size: 0.6rem !important; color: var(--muted) !important; margin-left: 2px; transition: transform 0.15s ease; }
.nav-link.role.open .nav-caret { transform: rotate(180deg); }
.nav-link.role.open { border-color: var(--brand); color: var(--brand); }
.row-kbd {
  font-family: var(--mono-font);
  font-size: 10.5px;
  color: var(--muted);
  border: 1px solid var(--line);
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 1px 5px;
}
.row-caret { transition: transform 0.15s ease; }
.row-caret.flipped { transform: rotate(180deg); }
.dd-row:hover .row-caret.flipped { transform: rotate(180deg); }
.lang-list {
  margin: 2px 0 4px 14px;
  padding-left: 6px;
  border-left: 1px solid var(--line);
}
.lang-list .dd-row { padding-top: 7px; padding-bottom: 7px; }
.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--body-text);
  font-size: 15px;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: color 0.16s ease, border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
}
.icon-btn:hover {
  color: var(--brand);
  border-color: var(--brand);
  transform: translateY(-1px);
}
.icon-btn.open {
  color: var(--brand);
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-soft);
}
.avatar-btn {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--brand);
  color: #ffffff;
  font-family: var(--display-font);
  font-weight: 600;
  font-size: 12.5px;
  letter-spacing: 0.03em;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}
.avatar-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 60, 255, 0.35);
}
.avatar-btn.open { box-shadow: 0 0 0 3px var(--brand-soft); }
.avatar-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--amber);
  border: 2px solid var(--bar-bg);
}
.dd-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 300px;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 7px;
  box-shadow: var(--shadow-panel);
  z-index: 50;
  transform-origin: top right;
  animation: nav-dd-in 0.16s ease;
}
.user-panel { width: 320px; }
.about-panel { width: 264px; }
@keyframes nav-dd-in {
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
.dd-section {
  padding: 10px 10px 5px;
  font-family: var(--mono-font);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
}
.dd-divider {
  height: 1px;
  background: var(--line);
  margin: 6px;
}
.dd-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  cursor: pointer;
  font-family: var(--body-font);
  font-size: 0.875rem;
  color: var(--body-text);
  text-align: left;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.dd-row:hover {
  background: var(--hover);
  color: var(--text-strong);
  transform: translateX(2px);
}
.row-icon {
  width: 18px;
  text-align: center;
  font-size: 0.85rem;
  color: var(--muted);
  flex-shrink: 0;
  transition: color 0.15s ease;
}
.dd-row:hover .row-icon { color: var(--brand); }
.row-icon-role { color: var(--brand); }
.row-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row-meta {
  font-family: var(--mono-font);
  font-size: 11px;
  color: var(--muted);
  white-space: nowrap;
}
.row-refresh {
  color: var(--signal);
  font-size: 0.72rem;
  margin-left: 2px;
}
.dd-row:hover .row-refresh { animation: nav-spin 1s linear infinite; }
.row-label-stack {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.row-label-stack > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row-sub {
  font-size: 11px;
  color: var(--muted);
}
.storage-cost-row .row-icon { color: var(--adm-warning, #d97706); }
@keyframes nav-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.row-go {
  font-size: 10px;
  color: var(--muted);
  transition: transform 0.15s ease, color 0.15s ease;
}
.dd-row:hover .row-go { transform: translateX(2px); color: var(--brand); }
.row-flag { width: 18px; text-align: center; flex-shrink: 0; }
.row-check { color: var(--brand); font-size: 11px; }
.row-active { background: var(--brand-soft); color: var(--brand); }
.row-active .row-icon { color: var(--brand); }
.row-danger,
.row-danger .row-icon { color: var(--danger); }
.row-danger:hover { background: rgba(217, 45, 32, 0.08); color: var(--danger); }

.theme-row :deep(.theme-toggle) {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: var(--body-text);
  font-family: var(--body-font);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.theme-row :deep(.theme-toggle i) {
  width: 18px;
  text-align: center;
  font-size: 0.85rem;
  color: var(--muted);
}
.theme-row :deep(.theme-toggle:hover) {
  background: var(--hover);
  color: var(--text-strong);
  transform: translateX(2px);
}

/* User card + subscription block */
.user-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px 4px;
}
.user-head-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--ink);
  border: 1px solid var(--line);
  color: var(--signal);
  display: grid;
  place-items: center;
  font-family: var(--display-font);
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
}
.user-head-meta { min-width: 0; display: flex; flex-direction: column; }
.user-head-name {
  font-family: var(--display-font);
  font-weight: 600;
  font-size: 13.5px;
  color: var(--text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-head-email {
  font-family: var(--mono-font);
  font-size: 11px;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 230px;
}
.sub-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: calc(100% - 8px);
  margin: 2px 4px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: var(--brand-soft);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.16s ease, transform 0.16s ease;
}
.sub-card:hover { border-color: var(--brand); transform: translateY(-1px); }
.sub-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--panel);
  border: 1px solid var(--line);
  display: grid;
  place-items: center;
  font-size: 13px;
  color: var(--muted);
  flex-shrink: 0;
}
.sub-icon .fa-crown { color: var(--amber); }
.sub-body { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 2px; }
.sub-title {
  font-family: var(--display-font);
  font-weight: 600;
  font-size: 13px;
  color: var(--text-strong);
}
.sub-ext { font-size: 10px; color: var(--muted); margin-left: 4px; }
.sub-card:hover .sub-ext { color: var(--brand); }
.sub-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  color: var(--body-text);
}
.sub-plan {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}
.sub-days {
  font-family: var(--mono-font);
  font-size: 11px;
  color: var(--muted);
  white-space: nowrap;
}
.sub-expiring,
.sub-expiring .sub-days { color: var(--amber); font-weight: 600; }
.sub-hint { font-size: 10.5px; color: var(--muted); margin-top: 3px; }
.sub-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 4px 2px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--warn-bg);
  color: var(--warn-text);
  font-size: 0.78rem;
  line-height: 1.35;
}
.sub-warning i { margin-top: 2px; }

/* ---------- Login button ---------- */
.login-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: var(--brand);
  color: #ffffff;
  font-family: var(--display-font);
  font-weight: 600;
  font-size: 0.84rem;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(0, 60, 255, 0.28);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 60, 255, 0.38);
}
.login-btn-compact { height: 34px; padding: 0 12px; }

/* ---------- Editor mode bar ---------- */
.editor-bar {
  background: var(--ink);
  border-bottom: 1px solid #26305e;
  color: #e6e9f7;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
}
.editor-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--signal), var(--brand));
}
.editor-bar-compact { height: 56px; }
/* Mobile editor bar: close is an X-only button, no text label */
.editor-bar-compact .editor-close span { display: none; }
.editor-bar-compact .editor-close { padding: 8px 11px; }
.editor-tag {
  font-family: var(--mono-font);
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);
  border: 1px solid #26305e;
  border-radius: 5px;
  padding: 4px 8px;
  white-space: nowrap;
  flex-shrink: 0;
}
.editor-drafts { flex: 1; min-width: 0; display: flex; align-items: center; }
.editor-close {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-family: var(--body-font);
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}
.editor-close:hover {
  background: var(--danger);
  border-color: var(--danger);
  transform: translateY(-1px);
}

/* ---------- Mobile top bar ---------- */
/* Mobile app header is hidden: bottom tabs + sheet cover navigation, search,
   credits and login on small screens (the header stays mounted so the mobile
   search ref keeps working for the bottom Caută tab). */
.mobile-top {
  display: none;
}
.brand-compact { padding: 5px 6px; }
.mobile-search {
  flex: none;
  min-width: 0;
  margin: 0 !important;
  max-width: none !important;
}
.mobile-spacer { flex: 1; }
.chip-value-mobile { font-size: 11.5px; }

/* ---------- Mobile bottom tab bar ---------- */
.mobile-bottom {
  background: var(--bar-bg);
  border-top: 1px solid var(--line);
  box-shadow: 0 -4px 16px rgba(10, 15, 44, 0.06);
  display: flex;
  align-items: stretch;
  height: calc(64px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  overflow: hidden;
}
/* Middle tabs scroll; Dashboard and Meniu stay pinned outside the scroller */
.tabs-scroll {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: stretch;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.tabs-scroll::-webkit-scrollbar { display: none; }
/* Scroller tabs size to their label (never truncated): the strip scrolls
   only when they don't all fit completely. */
.tabs-scroll .tab { min-width: max-content; }
/* Pinned tabs share one fixed width so Dashboard sits left, Meniu right,
   and the scroll strip exactly fills the space between them. The child
   combinator outranks the later `.tab` rule so the pin is never overridden. */
.mobile-bottom > .tab-pinned {
  flex: 0 0 84px;
  min-width: 0;
  max-width: 84px;
}
.mobile-bottom > .tab-pinned span {
  white-space: normal;
  text-align: center;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.tab {
  flex: 1 0 auto;
  min-width: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 4px;
  border: none;
  background: none;
  cursor: pointer;
  position: relative;
  color: var(--muted);
  font-family: var(--body-font);
  transition: color 0.15s ease;
}
.tab i { font-size: 17px; transition: transform 0.15s ease; }
.tab span {
  font-family: var(--mono-font);
  font-size: 9.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tab:hover { color: var(--text-strong); }
.tab:hover i { transform: translateY(-1px); }
.tab.active { color: var(--brand); }
.tab.active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 22px;
  height: 3px;
  border-radius: 0 0 3px 3px;
  background: var(--brand);
}

/* ---------- Mobile sheet ---------- */
/* The sheet is teleported to <body>, so it lives outside .main-navbar's scope.
   It defines its own tokens (light + .dark) instead of inheriting them —
   otherwise it falls back to page-level variables and renders washed out. */
.sheet-backdrop {
  --sh-ink: var(--adm-text-strong, #0a0f2c);
  --sh-panel: var(--adm-card, #ffffff);
  --sh-line: var(--adm-border, #e4e8f5);
  --sh-hover: var(--adm-card-head, #f2f4fb);
  --sh-strong: var(--adm-text-strong, #0a0f2c);
  --sh-body: var(--adm-text, #3a4160);
  --sh-muted: var(--adm-muted, #8a92b8);
  --sh-brand: var(--adm-accent, #003cff);
  --sh-brand-soft: var(--adm-accent-soft, #e6ecff);

  position: fixed;
  inset: 0;
  z-index: 9998; /* above page modals (1000-2000) and export widget (1200),
                   below search overlay (9999) and toasts (10000) */
  background: rgba(10, 15, 44, 0.6);
}
.sheet-backdrop.dark,
html.dark-mode .sheet-backdrop {
  --sh-panel: var(--adm-card, #141b3f);
  --sh-line: var(--adm-border, #26305e);
  --sh-hover: var(--adm-card-head, #1b2250);
  --sh-strong: var(--adm-text-strong, #e6e9f7);
  --sh-body: var(--adm-text, #c3c9e2);
  --sh-muted: var(--adm-muted, #8a92b8);
  --sh-brand: var(--adm-accent, #4d72ff);
  --sh-brand-soft: var(--adm-accent-soft, #1a2250);
}
.sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--sh-panel);
  border-top: 1px solid var(--sh-line);
  border-radius: 16px 16px 0 0;
  max-height: min(84vh, 700px);
  display: flex;
  flex-direction: column;
  padding-bottom: env(safe-area-inset-bottom);
  box-shadow: 0 -16px 48px rgba(10, 15, 44, 0.35);
  color: var(--sh-body);
}
.sheet .dd-section { color: var(--sh-muted); }
.sheet .sheet-user-name { color: var(--sh-strong); }
.sheet .sheet-user-email { color: var(--sh-muted); }
.sheet .sheet-avatar { background: var(--sh-ink); border-color: var(--sh-line); color: #00e5a0; }
.sheet .sub-card { background: var(--sh-brand-soft); border-color: transparent; }
.sheet .sub-card:hover { border-color: var(--sh-brand); }
.sheet .sub-icon { background: var(--sh-panel); border-color: var(--sh-line); }
.sheet .sub-title { color: var(--sh-strong); }
.sheet .sub-line { color: var(--sh-body); }
.sheet .row-label { color: var(--sh-strong); }
.sheet .dd-row { color: var(--sh-body); }
.sheet .dd-row:hover { background: var(--sh-hover); color: var(--sh-strong); }
.sheet .dd-divider { background: var(--sh-line); }
.sheet .theme-row { color: var(--sh-body); }
.sheet .row-icon { color: var(--sh-muted); }
.sheet .dd-row:hover .row-icon { color: var(--sh-brand); }
.sheet .row-check { color: var(--sh-brand); }
.sheet .dd-row.row-active { background: var(--sh-brand-soft); color: var(--sh-strong); }
.sheet .sub-warning { background: rgba(255, 184, 0, 0.12); border-color: rgba(255, 184, 0, 0.55); color: #ffd35c; }
.sheet-handle {
  width: 44px;
  height: 5px;
  border-radius: 999px;
  background: var(--line);
  margin: 10px auto 2px;
  flex-shrink: 0;
}
.sheet-scroll { overflow-y: auto; padding: 6px 12px 18px; }
.sheet-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 10px 6px;
}
.sheet-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--ink);
  border: 1px solid var(--line);
  color: var(--signal);
  display: grid;
  place-items: center;
  font-family: var(--display-font);
  font-weight: 700;
  font-size: 15px;
  flex-shrink: 0;
}
.sheet-user-meta { min-width: 0; display: flex; flex-direction: column; }
.sheet-user-name {
  font-family: var(--display-font);
  font-weight: 600;
  font-size: 15px;
  color: var(--text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sheet-user-email {
  font-family: var(--mono-font);
  font-size: 11px;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sheet-wps { width: 100%; padding: 2px 4px 6px; }
.sheet-wps :deep(.balance-button) {
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
}
.sheet-wps :deep(.balance-button .truncate) { max-width: none; }
.sheet-login { width: calc(100% - 8px); margin: 12px 4px 4px; justify-content: center; }

.sheet-enter-active,
.sheet-leave-active { transition: opacity 0.2s ease; }
.sheet-enter-active .sheet,
.sheet-leave-active .sheet { transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1); }
.sheet-enter-from,
.sheet-leave-to { opacity: 0; }
.sheet-enter-from .sheet,
.sheet-leave-to .sheet { transform: translateY(100%); }

/* ---------- Small screens ---------- */
@media (max-width: 560px) {
  .editor-tag { display: none; }
  /* Dropdown panels must fit a 390px viewport instead of overflowing right */
  .dd-panel {
    width: min(300px, calc(100vw - 24px));
    right: -8px;
  }
  .user-panel { width: min(320px, calc(100vw - 24px)); }
  .about-panel { width: min(264px, calc(100vw - 24px)); }
}

/* ---------- Workspace drawer (right slide-over, desktop + mobile) ---------- */
.wps-fab {
  position: fixed;
  right: 0;
  z-index: 2147483000;
  width: 44px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--adm-border, #e4e8f5);
  border-right: none;
  border-radius: 12px 0 0 12px;
  background: var(--adm-card, #fff);
  color: var(--adm-accent, #003cff);
  font-size: 1rem;
  cursor: grab;
  box-shadow: -6px 4px 18px rgba(10, 15, 44, 0.18);
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  padding: 0;
}
/* Mobile bottom nav (z-40) + sheet backdrop sit below the fab; on touch the
   fab must stay tappable above the bottom tab bar. */
@media (max-width: 1023px) {
  .wps-fab {
    /* Stick out from the screen edge so page-level horizontal overflow
       (100vw rows, wide tables) can never bury it off-screen. */
    right: max(8px, env(safe-area-inset-right, 0px));
    border-right: 1px solid var(--line, #e4e8f5);
    border-radius: 12px;
  }
}
.wps-fab:hover { background: var(--adm-card-head, var(--hover)); }
.wps-fab.dragging { cursor: grabbing; opacity: 0.9; }
.wps-fab-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00c853;
}
.wps-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(10, 15, 44, 0.45);
  display: flex;
  justify-content: flex-end;
}
.wps-drawer {
  width: min(480px, 100vw);
  height: 100dvh;
  background: var(--adm-card, #fff);
  border-left: 1px solid var(--adm-border, var(--line));
  box-shadow: -18px 0 48px rgba(10, 15, 44, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.wps-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  flex-shrink: 0;
}
.wps-drawer-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--display-font, var(--body-font));
  font-weight: 700;
  font-size: 1rem;
  color: var(--text-strong, var(--body-text));
}
.wps-drawer-title i { color: var(--adm-accent, var(--brand)); }
.wps-drawer-close {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
}
.wps-drawer-close:hover { background: var(--adm-card-head, var(--hover)); color: var(--adm-text-strong, var(--text-strong)); }
.wps-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  min-height: 0;
}
.wps-drawer-body .wps-dashboard-root { min-height: 0; }
.wps-drawer-enter-active, .wps-drawer-leave-active { transition: opacity 0.2s ease; }
.wps-drawer-enter-active .wps-drawer, .wps-drawer-leave-active .wps-drawer { transition: transform 0.24s ease; }
.wps-drawer-enter-from, .wps-drawer-leave-to { opacity: 0; }
.wps-drawer-enter-from .wps-drawer, .wps-drawer-leave-to .wps-drawer { transform: translateX(100%); }
@media (max-width: 400px) {
  .mobile-top .brand-word { display: none; }
}

/* ---------- Notifications (bell FAB + drawer, mirrors workspace selector) ---------- */
.notif-fab-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #d92d20;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
}
.notif-unread-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #d92d20;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
}
.notif-head-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.notif-clear {
  background: none;
  border: none;
  color: var(--adm-muted, var(--muted));
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
}
.notif-clear:hover { color: var(--adm-text-strong, var(--text-strong)); background: var(--adm-card-head, var(--hover)); }
.notif-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.notif-empty {
  text-align: center;
  color: var(--muted);
  padding: 40px 16px;
}
.notif-empty i {
  display: block;
  font-size: 1.8rem;
  margin-bottom: 10px;
}
.notif-empty p { margin: 0; font-size: 0.9rem; }
.notif-card {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel, #fff);
}
.notif-warning { border-left: 4px solid #f59e0b; }
.notif-danger { border-left: 4px solid #d92d20; }
.notif-success { border-left: 4px solid #0b9e6f; }
.notif-info { border-left: 4px solid var(--brand, #003cff); }
.notif-icon { font-size: 1rem; margin-top: 2px; color: var(--muted); flex-shrink: 0; }
.notif-warning .notif-icon { color: #f59e0b; }
.notif-danger .notif-icon { color: #d92d20; }
.notif-success .notif-icon { color: #0b9e6f; }
.notif-info .notif-icon { color: var(--brand, #003cff); }
.notif-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.notif-text {
  font-size: 0.88rem;
  color: var(--text-strong, var(--body-text));
  line-height: 1.4;
}
.notif-sub { font-size: 0.78rem; color: var(--muted); }
.notif-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.notif-review {
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  background: var(--brand, #003cff);
  color: #fff;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
}
.notif-review:hover { filter: brightness(1.1); }
.notif-dismiss {
  background: transparent;
  border: none;
  color: var(--muted);
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
}
.notif-dismiss:hover { background: var(--hover); color: var(--text-strong); }
</style>
